import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  faAddressCard,
  faFolder,
  faLayerGroup,
  faListCheck,
  faLocationArrow,
  faRoad,
  faTruck,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Collapse } from "@mui/material";
import VersionPanel from "../../VersionPanel";

const drawerWidth = 240;
styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));
styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const ListItemTextStyled = styled(ListItemText)({});

export default function PersistentDrawerLeft({ open }: { open: boolean }) {
  const [locationOpen, setLocationOpen] = React.useState(false);
  const [bidPackOpen, setBidPackOpen] = React.useState(false);
  const [transportNetworkOpen, setTransportNetworkOpen] = React.useState(false);
  const [routeDataOpen, setRouteDataOpen] = React.useState(false);
  const [validationBacklogOpen, setValidationBacklogOpen] = React.useState(false);

  const handleLocationClick = () => {
    setLocationOpen(!locationOpen);
  };

  const handleBidPackClick = () => {
    setBidPackOpen(!bidPackOpen);
  };

  const handleTransportNetworkClick = () => {
    setTransportNetworkOpen(!transportNetworkOpen);
  };

  const handleRouteDataClick = () => {
    setRouteDataOpen(!routeDataOpen);
  };

  const handleValidationBacklogClick = () => {
    setValidationBacklogOpen(!validationBacklogOpen);
  };

  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", position: "absolute", top: 0 }}>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <Toolbar />
        <Box pb={3} sx={{ overflow: "auto" }}>
          <List
            sx={{
              width: "100%",
              maxWidth: 360,
              top: -8,
              bgcolor: "background.paper",
              "& .MuiListItemButton-root:hover": {
                bgcolor: theme.palette.primary.main,
              },
            }}
            component="nav"
          >
            <ListItemButton component={Link} to="/Carriers">
              <ListItemIcon>
                <FontAwesomeIcon icon={faTruck} style={{ color: theme.palette.text.primary }} />
              </ListItemIcon>
              <ListItemTextStyled
                primary="Carrier"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
            </ListItemButton>

            <ListItemButton onClick={handleLocationClick}>
              <ListItemIcon>
                <FontAwesomeIcon
                  icon={faLocationArrow}
                  style={{ color: theme.palette.text.primary }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Location"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
              {locationOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={locationOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/Suppliers">
                  <ListItemText
                    primary="Supplier"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/Plants">
                  <ListItemText
                    primary="Plant"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/EDCs">
                  <ListItemText
                    primary="EDC"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/ODCs">
                  <ListItemText
                    primary="ODC & Other"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
              </List>
            </Collapse>

            <ListItemButton component={Link} to="/Contacts">
              <ListItemIcon>
                <FontAwesomeIcon
                  icon={faAddressCard}
                  style={{ color: theme.palette.text.primary }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Contact"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
            </ListItemButton>

            <ListItemButton onClick={handleBidPackClick}>
              <ListItemIcon>
                <FontAwesomeIcon
                  icon={faLayerGroup}
                  style={{ color: theme.palette.text.primary }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Bid Pack & Rate Agrmt."
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
              {bidPackOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={bidPackOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/BidPacks">
                  <ListItemText
                    primary="Bid Pack"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/RateAgreements">
                  <ListItemText
                    primary="Rate Agreement"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
              </List>
            </Collapse>

            <ListItemButton onClick={handleTransportNetworkClick}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faTruck} style={{ color: theme.palette.text.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Transport Network"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
              {transportNetworkOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={transportNetworkOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/SPDRS">
                  <ListItemText
                    primary="Inbound Network"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/ReturnNetwork">
                  <ListItemText
                    primary="Return Network"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/TTProtections">
                  <ListItemText
                    primary="Transit Time Protection"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
              </List>
            </Collapse>

            <ListItemButton onClick={handleRouteDataClick}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faRoad} style={{ color: theme.palette.text.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Route Data"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
              {routeDataOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={routeDataOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/FTLRoutes">
                  <ListItemText
                    primary="FTL Routes"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/ProposedFTLRoutes">
                  <ListItemText
                    primary="Proposed FTL Routes"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
              </List>
            </Collapse>

            <ListItemButton component={Link} to="/Projects">
              <ListItemIcon>
                <FontAwesomeIcon icon={faFolder} style={{ color: theme.palette.text.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Project"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
            </ListItemButton>

            <ListItemButton onClick={handleValidationBacklogClick}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faListCheck} style={{ color: theme.palette.text.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Validation Backlog"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
              {validationBacklogOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={validationBacklogOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} component={Link} to="/PDSRS">
                  <ListItemText
                    primary="PDSR Validation"
                    style={{ color: theme.palette.text.primary }}
                    primaryTypographyProps={{ fontSize: "small" }}
                  />
                </ListItemButton>
              </List>
            </Collapse>

            {/* HELP */}
            <ListItemButton component={Link} to="/Help">
              <ListItemIcon>
                <FontAwesomeIcon icon={faQuestion} style={{ color: theme.palette.text.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Help"
                style={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ fontSize: "small" }}
              />
            </ListItemButton>
          </List>
        </Box>
        <VersionPanel />
      </Drawer>
    </Box>
  );
}
